import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { getPlanInfo } from '../actions/subscriptionAction';
import { billingRegion } from '../utils/region';
import './Pricing.css';

const FREE_FEATURES = [
  'Build unlimited visions, milestones and actions',
  'Up to 25 active todos',
  'Up to 3 habits with streak tracking',
  'Journal and Library documents',
];

const PRO_FEATURES = [
  'Everything in Free',
  'Dmyor Assist: AI that plans, breaks down and drafts',
  'Focus Mode: timed deep-work sessions on any todo',
  'Unlimited todos and habits across every vision',
  'AI-generated documents in the Library',
  'Execution summaries of where your vision stands',
];

export default function Pricing() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((s) => s.user || {});
  const [region, setRegion] = useState(null); // { symbol, monthly, yearly, ... }
  const [interval, setInterval] = useState('monthly');

  useEffect(() => {
    dispatch(getPlanInfo())
      .then((d) => {
        const r = d.regions?.[billingRegion()] || d.regions?.INTL || null;
        setRegion(r);
      })
      .catch(() => {});
  }, [dispatch]);

  const cur = region?.symbol || '$';
  const price = region ? (interval === 'yearly' ? region.yearly : region.monthly) : null;
  const saved = region?.yearly && region?.monthly
    ? Math.round((1 - (Number(region.yearly) / (Number(region.monthly) * 12))) * 100)
    : 0;

  const ctaTarget = isAuthenticated ? '/dashboard/upgrade' : '/signup';

  return (
    <div className="pr-page">
      <Helmet>
        <title>Pricing — dmyor</title>
        <link rel="canonical" href="https://www.dmyor.com/pricing" />
        <meta name="description" content="dmyor pricing. Start free and build your visions. Upgrade to Pro for Dmyor Assist, Focus Mode, and unlimited todos and habits." />
        <meta property="og:title" content="Pricing — dmyor" />
        <meta property="og:description" content="Start free. Upgrade to Pro for Dmyor Assist, Focus Mode and unlimited execution." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dmyor.com/pricing" />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing — dmyor" />
        <meta name="twitter:description" content="Start free. Upgrade to Pro for Dmyor Assist, Focus Mode and unlimited execution." />
        <meta name="twitter:image" content="https://dmyor.com/images/og-image.png" />
      </Helmet>

      <div className="pr-wrap">
        <span className="pr-eyebrow">Pricing</span>
        <h1 className="pr-title">Start free. Go Pro when you're ready.</h1>
        <p className="pr-sub">
          Everyone can plan and track for free. Pro unlocks the tools that help you actually execute.
        </p>

        {region && (
          <div className="pr-toggle" role="tablist">
            <button
              className={`pr-toggle-opt${interval === 'monthly' ? ' is-active' : ''}`}
              onClick={() => setInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`pr-toggle-opt${interval === 'yearly' ? ' is-active' : ''}`}
              onClick={() => setInterval('yearly')}
            >
              Yearly{saved > 0 && <span className="pr-toggle-save">Save {saved}%</span>}
            </button>
          </div>
        )}

        <div className="pr-cards">
          {/* Free */}
          <div className="pr-card">
            <div className="pr-card-head">
              <h2 className="pr-card-name">Free</h2>
              <div className="pr-card-price"><span className="pr-price-amt">{cur}0</span></div>
              <p className="pr-card-note">Everything you need to start.</p>
            </div>
            <ul className="pr-feature-list">
              {FREE_FEATURES.map((f) => (
                <li key={f}><span className="pr-tick" />{f}</li>
              ))}
            </ul>
            <Link to={ctaTarget} className="pr-cta pr-cta--ghost">Get started</Link>
          </div>

          {/* Pro */}
          <div className="pr-card pr-card--pro">
            <div className="pr-badge">Most popular</div>
            <div className="pr-card-head">
              <h2 className="pr-card-name">Pro</h2>
              <div className="pr-card-price">
                {price ? (
                  <>
                    <span className="pr-price-amt">{cur}{price}</span>
                    <span className="pr-price-per">/ {interval === 'yearly' ? 'year' : 'month'}</span>
                  </>
                ) : (
                  <span className="pr-price-amt">—</span>
                )}
              </div>
              <p className="pr-card-note">For people serious about making it real.</p>
            </div>
            <ul className="pr-feature-list">
              {PRO_FEATURES.map((f) => (
                <li key={f}><span className="pr-tick pr-tick--pro" />{f}</li>
              ))}
            </ul>
            <Link to={ctaTarget} className="pr-cta pr-cta--solid">
              {isAuthenticated ? 'Upgrade to Pro' : 'Start with Pro'}
            </Link>
          </div>
        </div>

        <p className="pr-fine">Cancel anytime. Your plans and data are always yours.</p>
      </div>
    </div>
  );
}
